import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle, Activity,
  Globe, ChevronRight, RefreshCw, AlertTriangle,
  Terminal, Copy, Check,
} from 'lucide-react';
import api from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SkopeScan {
  id: string;
  url: string;
  title: string | null;
  status: string;
  total_elements: number;
  critical_count: number;
  alta_count: number;
  media_count: number;
  baja_count: number;
  compliance_pct: number;
  entity_id: string | null;
  project_id: string | null;
  browser: string;
  skope_version: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: any }> = {
  scanned:   { label: 'Scanned',   icon: Activity,     cls: 'text-blue-400 bg-blue-400/10' },
  reported:  { label: 'Reported',  icon: CheckCircle,  cls: 'text-indigo-400 bg-indigo-400/10' },
  generated: { label: 'Generated', icon: CheckCircle,  cls: 'text-violet-400 bg-violet-400/10' },
  executed:  { label: 'Executed',  icon: CheckCircle,  cls: 'text-emerald-400 bg-emerald-400/10' },
  k6_ready:  { label: 'K6 Ready', icon: Activity,     cls: 'text-yellow-400 bg-yellow-400/10' },
  error:     { label: 'Error',     icon: AlertTriangle, cls: 'text-red-400 bg-red-400/10' },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
}

function hostname(url: string): string {
  try { return new URL(url).hostname; } catch { return url; }
}

function complianceColor(pct: number): string {
  if (pct >= 90) return 'text-emerald-400';
  if (pct >= 70) return 'text-yellow-400';
  return 'text-red-400';
}

function complianceBg(pct: number): string {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 70) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ── Onboarding ────────────────────────────────────────────────────────────────

function CopyableCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 font-mono text-sm group">
      <span className="text-violet-300 flex-1">{code}</span>
      <button
        onClick={copy}
        className="text-slate-600 group-hover:text-slate-400 hover:text-white transition-colors"
        title="Copiar"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function OnboardingEmpty() {
  return (
    <div className="max-w-lg mx-auto py-16 text-center">
      <div className="p-5 bg-slate-800/60 rounded-2xl inline-block mb-6">
        <Shield className="w-10 h-10 text-violet-400" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">Sin scans de accesibilidad aún</h3>
      <p className="text-slate-400 text-sm mb-8 leading-relaxed">
        Usa el CLI de Skope para analizar cualquier URL con WCAG/ARIA y enviar
        los resultados a FlowSpec QA.
      </p>

      {/* Steps */}
      <div className="text-left space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 text-xs flex items-center justify-center font-bold">1</span>
            <span className="text-sm font-medium text-slate-300">Instala Skope CLI</span>
          </div>
          <CopyableCode code="npm install -g @qasmart/skope" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 text-xs flex items-center justify-center font-bold">2</span>
            <span className="text-sm font-medium text-slate-300">Escanea una URL</span>
          </div>
          <CopyableCode code="skope scan https://tu-sitio.com" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 text-xs flex items-center justify-center font-bold">3</span>
            <span className="text-sm font-medium text-slate-300">Envía los resultados</span>
          </div>
          <CopyableCode code="skope push" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-slate-700 text-slate-400 text-xs flex items-center justify-center font-bold">✦</span>
            <span className="text-sm font-medium text-slate-400">Opcional: genera suite Playwright</span>
          </div>
          <CopyableCode code="skope generate && skope push" />
        </div>
      </div>

      <p className="text-xs text-slate-600 mt-8">
        Los resultados aparecerán aquí automáticamente después del{' '}
        <code className="text-slate-500">push</code>.
      </p>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SkopeDashboard() {
  const navigate = useNavigate();
  const [scans, setScans]   = useState<SkopeScan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);
  const [page, setPage]     = useState(1);
  const [total, setTotal]   = useState(0);
  const PAGE_SIZE = 20;

  async function load(p = 1) {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listScans({ page: p, page_size: PAGE_SIZE });
      setScans(res.items ?? []);
      setTotal(res.total ?? 0);
      setPage(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Error al cargar scans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // ── KPIs ──────────────────────────────────────────────────────────────────────
  const avgCompliance = scans.length
    ? scans.reduce((s, sc) => s + sc.compliance_pct, 0) / scans.length
    : 0;
  const totalCriticals = scans.reduce((s, sc) => s + sc.critical_count, 0);
  const withIssues     = scans.filter(sc => sc.critical_count > 0).length;

  return (
    <div className="min-h-screen bg-background-dark text-white p-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/10 rounded-lg">
            <Shield className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Skope — Accesibilidad WCAG</h1>
            <p className="text-sm text-slate-400">Historial de scans de accesibilidad y ARIA</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://github.com/pablomra/skope"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white
                       bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <Terminal className="w-4 h-4" />
            Docs CLI
          </a>
          <button
            onClick={() => load(page)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white
                       bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Total Scans</p>
          <p className="text-3xl font-bold text-violet-400">{total}</p>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Compliance Prom.</p>
          <p className={`text-3xl font-bold ${complianceColor(avgCompliance)}`}>
            {scans.length ? `${avgCompliance.toFixed(0)}%` : '—'}
          </p>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Críticos Totales</p>
          <p className={`text-3xl font-bold ${totalCriticals > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
            {totalCriticals}
          </p>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Con Issues</p>
          <p className={`text-3xl font-bold ${withIssues > 0 ? 'text-yellow-400' : 'text-emerald-400'}`}>
            {withIssues}
          </p>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Cargando scans...</span>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      ) : scans.length === 0 ? (
        <OnboardingEmpty />
      ) : (
        <>
          {/* ── Table ── */}
          <div className="bg-surface-dark border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">URL</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-40">Compliance</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Crítico</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Alta</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Media</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Elementos</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {scans.map(scan => {
                  const st = STATUS_CONFIG[scan.status] ?? STATUS_CONFIG.error;
                  const StatusIcon = st.icon;
                  return (
                    <tr
                      key={scan.id}
                      onClick={() => navigate(`/scans/${scan.id}`)}
                      className="hover:bg-slate-800/40 cursor-pointer transition-colors"
                    >
                      {/* URL */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          <span className="text-sm text-white font-medium truncate max-w-[220px]">
                            {hostname(scan.url)}
                          </span>
                        </div>
                        {scan.title && (
                          <p className="text-xs text-slate-500 truncate max-w-[220px] pl-5">
                            {scan.title}
                          </p>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>
                          <StatusIcon className="w-3 h-3" />
                          {st.label}
                        </span>
                      </td>

                      {/* Compliance bar */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${complianceBg(scan.compliance_pct)}`}
                              style={{ width: `${scan.compliance_pct}%` }}
                            />
                          </div>
                          <span className={`text-sm font-bold font-mono w-12 text-right ${complianceColor(scan.compliance_pct)}`}>
                            {scan.compliance_pct.toFixed(0)}%
                          </span>
                        </div>
                      </td>

                      {/* Crítico */}
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-semibold font-mono ${
                          scan.critical_count === 0 ? 'text-emerald-400' :
                          scan.critical_count < 5   ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {scan.critical_count}
                        </span>
                      </td>

                      {/* Alta */}
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-mono ${
                          scan.alta_count === 0 ? 'text-slate-600' : 'text-orange-400'
                        }`}>
                          {scan.alta_count}
                        </span>
                      </td>

                      {/* Media */}
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-mono ${
                          scan.media_count === 0 ? 'text-slate-600' : 'text-yellow-400/70'
                        }`}>
                          {scan.media_count}
                        </span>
                      </td>

                      {/* Total elementos */}
                      <td className="px-4 py-3 text-center text-sm font-mono text-slate-400">
                        {scan.total_elements}
                      </td>

                      {/* Fecha */}
                      <td className="px-4 py-3 text-right text-xs text-slate-500">
                        {fmtDate(scan.created_at)}
                      </td>

                      {/* Arrow */}
                      <td className="px-4 py-3 text-slate-600">
                        <ChevronRight className="w-4 h-4" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-400">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total} scans
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => load(page - 1)}
                  className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg
                             disabled:opacity-40 hover:bg-slate-700 transition-colors"
                >
                  Anterior
                </button>
                <button
                  disabled={page * PAGE_SIZE >= total}
                  onClick={() => load(page + 1)}
                  className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg
                             disabled:opacity-40 hover:bg-slate-700 transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
